import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { accentForRole, useTheme } from '../theme';
import Icon from './Icon';
import NotificationBell from './NotificationBell';
import { LiveProvider } from '../live/LiveContext';
import Sidebar, { navGroups } from './Sidebar';
import UpcomingInterviewsNotice from './UpcomingInterviewsNotice';

const COLLAPSE_KEY = 'sidebar-collapsed';

function readCollapsed() {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Group + page label for the current path (longest matching nav entry wins). */
function useBreadcrumb(user, pathname) {
  let best = null;
  navGroups(user).forEach((group) => {
    const entries = [
      ...group.items,
      ...(group.categories || []).map((c) => ({ to: c.path, label: c.label })),
    ];
    entries.forEach((item) => {
      const hit = item.end || item.to === '/'
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (hit && (!best || item.to.length > best.to.length)) {
        best = { to: item.to, label: item.label, group: group.label || '' };
      }
    });
  });
  return best;
}

function Layout({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const crumb = useBreadcrumb(user, location.pathname);

  useEffect(() => {
    document.documentElement.dataset.accent = accentForRole(user?.role);
  }, [user?.role]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // Preference just won't persist if storage is blocked.
      }
      return next;
    });
  }, []);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Close the mobile drawer after navigating.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isDark = theme === 'dark';

  return (
    <LiveProvider>
    <div className={`app-shell${collapsed ? ' is-sidebar-collapsed' : ''}`}>
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onToggleCollapsed={toggleCollapsed}
        onCloseMobile={closeMobile}
      />
      {mobileOpen && <div className="side-backdrop" onClick={closeMobile} aria-hidden="true" />}
      <div className="app-content">
        <header className="app-topbar">
          <button
            type="button"
            className="topbar-icon-btn mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Icon name="menu" size={19} />
          </button>
          <nav className="topbar-crumbs" aria-label="Breadcrumb">
            {crumb?.group && <span className="topbar-crumb-group">{crumb.group}</span>}
            {crumb?.group && <Icon name="chevronRight" size={14} className="topbar-crumb-sep" />}
            <span className="topbar-crumb-page">{crumb?.label || 'Satit & Cruce'}</span>
          </nav>
          <div className="topbar-actions">
            {(user?.role === 'superadmin' || user?.role === 'admin') && <NotificationBell />}
            <button
              type="button"
              className="topbar-icon-btn"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              <Icon name={isDark ? 'sun' : 'moon'} size={18} />
            </button>
          </div>
        </header>
        <UpcomingInterviewsNotice />
        <main className="app-main">
          <div className="container">{children}</div>
        </main>
      </div>
    </div>
    </LiveProvider>
  );
}

export default Layout;
